<agr-page-detail
  [pageTitle]="pageTitle$ | async"
  [isEmpty]="
    planetProofReport?.cropFields?.length === 0 &&
    !calculationIncomplete &&
    planetProofReport?.totalHectares === 0
  "
  [emptyTitle]="'noAdvices' | translate"
  [emptyDescription]="'noFields' | translate"
  [hasPrimaryAction]="true"
  primaryActionIcon="print"
  [primaryActionText]="'printReport' | translate"
  (primaryAction)="print()"
  (backAction)="back()"
  [buttonItems]="buttonItems$ | async"
  (buttonAction)="buttonAction($event)"
>
  <agr-print-page
    [footerText]="'gbmWarningFooter' | translate"
    [relatedPeople]="relatedPeople$ | async"
    [marginLeft]="true"
    [showPageNumber]="false"
  >
    @if ((planetProofReport$ | async) !== null) {
      <div class="planet-proof-report">
        <div>
          <app-general-info [reportForLabel]="'reportFor'"></app-general-info>
        </div>
        <!-- GBM warning -->
        <div class="notification notification-warning layout--margin-horizontal">
          <mat-icon>warning</mat-icon>
          <strong class="text--warning"> {{ 'gbmWarningTitle' | translate }}</strong>
          <br />
          <span [innerHTML]="'gbmWarningDescription' | translate"></span>
        </div>
        @if (calculationIncomplete) {
          <div
            class="notification notification-warning layout--margin-horizontal"
            role="alert"
            data-test="planetProofIncomplete"
          >
            <mat-icon>warning</mat-icon>
            <strong>{{ 'planetProofReport.incompleteTitle' | translate }}</strong
            ><br />
            <span>{{ 'planetProofReport.incompleteDescription' | translate }}</span>
          </div>
        }
        <!-- cropField overview -->
        <div class="page-break-after">
          <div>
            <h2 class="text--h3 layout--margin-horizontal">
              {{ 'cropFieldsTitle' | translate }}
            </h2>
            <div class="agr-table-name layout--margin-horizontal">
              {{ 'fieldThatTakePartInPlanetProof' | translate }}:
            </div>
            @if (cropFieldOverviewPlanetProofTableConfig?.dataSource?.filteredData?.length) {
              <div class="spacing--margin-half-vertical">
                <agr-table
                  [config]="cropFieldOverviewPlanetProofTableConfig"
                  data-test="cropFieldOverviewPlanetProofTable"
                ></agr-table>
              </div>
            } @else {
              <p class="layout--margin-horizontal">{{ 'noFieldsFound' | translate }}.</p>
            }
          </div>
          <div>
            <div class="agr-table-name layout--margin-horizontal">
              {{ 'otherFields' | translate }}:
            </div>
            @if (cropFieldOverviewOtherTableConfig?.dataSource?.filteredData?.length) {
              <div class="spacing--margin-half-vertical">
                <agr-table
                  [config]="cropFieldOverviewOtherTableConfig"
                  data-test="cropFieldOverviewOtherTable"
                ></agr-table>
              </div>
            } @else {
              <p class="layout--margin-horizontal">{{ 'noFieldsFound' | translate }}.</p>
            }
          </div>
        </div>
        <!-- totals overview (bedrijfsoverzicht) -->
        <div class="company-overview">
          <h2 class="text--h3 layout--margin-horizontal">
            {{ 'companyOverviewTitle' | translate }}
          </h2>
          @if (cropTableConfig?.dataSource?.filteredData?.length) {
            <div class="spacing--margin-half-vertical">
              <agr-table [config]="cropTableConfig" data-test="companyOverviewTable"></agr-table>
            </div>
          } @else {
            <div class="layout--margin-horizontal">{{ 'noItemsFound' | translate }}.</div>
          }
        </div>
        <!-- 3 panels: 1. extra bonus -->
        <div class="notification notification-info layout--margin-horizontal">
          <mat-icon>info</mat-icon>
          <strong class="text--info"> {{ 'explainationCropNormTitle' | translate }}</strong>
          <br />
          <ul>
            @if ((cropYear$ | async) >= CropYearCalculationChange) {
              <li>{{ 'explainationCropNorm10points' | translate }}</li>
              <li>{{ 'explainationCropNorm7points' | translate }}</li>
              <li>{{ 'explainationCropNorm4points' | translate }}</li>
              <li>{{ 'explainationCropNorm2points' | translate }}</li>
              <li>{{ 'explainationCropNorm1point' | translate }}</li>
            } @else {
              <li>{{ 'explainationCropNorm2pointsPre2026' | translate }}</li>
              <li>{{ 'explainationCropNorm1pointPre2026' | translate }}</li>
            }
          </ul>
        </div>
        <!-- 3 panels: 2. avg glysofaat -->
        <div class="notification notification-info layout--margin-horizontal">
          <mat-icon>info</mat-icon>
          <strong class="text--info"> {{ 'explainationGlyphosateTitle' | translate }}</strong>
          <br />
          <ul>
            @if ((cropYear$ | async) >= CropYearCalculationChange) {
              <li>{{ 'explainationGlyphosate1point' | translate }}</li>
              <li>{{ 'explainationGlyphosate2points' | translate }}</li>
              <li>{{ 'explainationGlyphosate3points' | translate }}</li>
            } @else {
              <li>{{ 'explainationGlyphosate1pointPre2026' | translate }}</li>
              <li>{{ 'explainationGlyphosate2pointsPre2026' | translate }}</li>
              <li>{{ 'explainationGlyphosate3pointsPre2026' | translate }}</li>
            }
          </ul>
        </div>
        <!-- 3 panels: 3. glysofaat malus -->
        <div class="page-break-after notification notification-warning layout--margin-horizontal">
          <mat-icon>info</mat-icon>
          <strong class="text--warning"> {{ 'averageGlyphosateUsageTitle' | translate }}</strong>
          <br />
          @if (usingArableReport) {
            <span>{{ 'companyGlyphosateScopeExplanation' | translate }}</span
            ><br />
          }
          @if (averageGlyphosateUsage !== null) {
            <span data-test="glyphosateAverage">
              {{ averageGlyphosateUsage | agrNumber: '1.2-5' }}
              {{ 'averageGlyphosateUsageText' | translate }}
              {{ 'malusPoints' | translate }}:
              {{ planetProofReport?.glyphosateMalusPoints }}
            </span>
          } @else {
            <span>{{ 'planetProofReport.notCalculated' | translate }}</span>
          }
        </div>
        <!-- cropField details with summed "saldo" and total at the bottom -->
        @if (usingArableReport) {
          <div class="layout--margin-horizontal">
            <h2 class="text--h3">{{ 'activeIngredients' | translate }}</h2>
            @for (crop of planetProofReport?.crops; track crop.cropId) {
              @if (crop.ingredients?.length) {
                <h3>{{ crop.cropName }}</h3>
                <ul>
                  @for (ingredient of crop.ingredients ?? []; track ingredient) {
                    <li>
                      {{ ingredient.name }}
                      @if (ingredient.forbidden) {
                        <strong class="text--warning">
                          {{ 'ingredientForbiddenInPlanetProof' | translate }}
                        </strong>
                      }
                    </li>
                  }
                </ul>
              }
            }
          </div>
        }
        <div>
          <h2 class="text--h3 layout--margin-horizontal">
            {{ 'cropFieldsDetailTitle' | translate }}
          </h2>
          @for (cropFieldTable of cropFieldTables; track cropFieldTable.cropFieldId) {
            <div>
              <div class="spacing--margin-half-vertical">
                <h3 class="agr-table-name layout--margin-horizontal">
                  {{ cropFieldTable.title }}
                </h3>
                @if (cropFieldTable.cropNormTitle.length) {
                  <p class="layout--margin-horizontal">
                    <em>{{ 'cropNorm' | translate }}:</em> {{ cropFieldTable.cropNormTitle }}
                  </p>
                }
                @if (
                  cropFieldTable.totalActiveMatter !== null &&
                  cropFieldTable.cropNorm < cropFieldTable.totalActiveMatter
                ) {
                  <div class="notification notification-warning layout--margin-horizontal">
                    <mat-icon>warning</mat-icon>
                    <strong class="text--warning">
                      {{ 'exceedingCropNormWarning' | translate }}</strong
                    >
                  </div>
                }
                @if (cropFieldTable.tableConfig?.dataSource?.filteredData?.length) {
                  <div class="spacing--margin-half-vertical">
                    <agr-table
                      [config]="cropFieldTable.tableConfig"
                      data-test="cropFieldTable"
                    ></agr-table>
                  </div>
                } @else {
                  <p class="layout--margin-horizontal">
                    {{
                      (!cropFieldTable.hasDetails || calculationIncomplete
                        ? 'planetProofReport.detailsUnavailable'
                        : 'planetProofReport.noCropProtectionTreatments'
                      ) | translate
                    }}
                  </p>
                }
                <p class="layout--margin-horizontal text--right">
                  <strong>
                    <em>{{ 'balanceBonusMinusMalus' | translate }}:</em>
                    {{ cropFieldTable.sumTotalBonusMalus ?? '—' }}
                  </strong>
                </p>
              </div>
            </div>
          }
          @if (!cropFieldTables?.length) {
            <div>
              <div class="layout--margin-horizontal">{{ 'noItemsFound' | translate }}.</div>
            </div>
          }
        </div>
      </div>
    }
  </agr-print-page>
</agr-page-detail>
